import waitForAccountToBeStarted from "owa-account-source-list/lib/accountStateChanged/waitForAccountToBeStarted";
import { logGreyError } from "owa-analytics";
import { lazyLoadItemCLPInfo, lazyGetAttachmentLabels } from "owa-mail-protection";
import { getAccountScopeUserSettings } from "owa-session-store/lib/selectors/getAccountScopeUserSettings";
import type { MailboxInfo } from "owa-client-types";
import type AttachmentId from "owa-service/lib/contract/AttachmentId";
import type { ClientItem } from "owa-mail-store";
export interface CLPInfo {
    clpLabelId: string;
    tenantId: string;
}
let cachedCLPInfo: CLPInfo[] | undefined;
let currItemId: string | undefined;

const ACCOUNT_START_WAIT_TIMEOUT = 3000;

export async function getCLPInfo(itemId: string, mailboxInfo: MailboxInfo, attachmentIds: AttachmentId[] | undefined): Promise<CLPInfo[] | undefined> {
    try {
        // If the attachmentIds are undefined, we cannot retrieve the CLP info accurately
        if (!attachmentIds) {
            logGreyError("InaccurateCLPInfo_AttachmentIdsUndefined", mailboxInfo);
            return undefined;
        }
        
        // Use cached CLP info if available and the global item ID hasn't changed
        if (itemId === currItemId && cachedCLPInfo) {
            return cachedCLPInfo;
        }
        
        currItemId = itemId;
        
        // Fetch CLP info for the item
        const itemCLPInfo = await lazyLoadItemCLPInfo.importAndExecute(itemId);
        const clpLabelId = itemCLPInfo?.selectedLabel?.id;
        
        // Wait for the account to be started
        const isStarted = await waitForAccountToBeStarted(mailboxInfo, ACCOUNT_START_WAIT_TIMEOUT);
        if (!isStarted) {
            logGreyError("ErrorLoadingClpInfo_AccountNotStarted", mailboxInfo);
            return undefined;
        }
        
        // Get the tenant ID from the mailbox info
        const tenantId = getAccountScopeUserSettings(mailboxInfo)?.SessionSettings?.ExternalDirectoryTenantGuid;
        
        // Attachment labels
        const attachmentClpLabelIds: string[] = [];
        if (attachmentIds.length > 0) {
            const getAttachmentLabels = await lazyGetAttachmentLabels.import();
            const attachmentLabelsReponse = await getAttachmentLabels(attachmentIds, mailboxInfo);
            if (!attachmentLabelsReponse ||
                !attachmentLabelsReponse.Body ||
                !attachmentLabelsReponse.Body.ResponseMessages ||
                !attachmentLabelsReponse.Body.ResponseMessages.Items ||
                attachmentLabelsReponse.Body.ResponseMessages.Items.length === 0) {
                logGreyError("InaccurateCLPInfo_GetAttachmentLabelsFailed", mailboxInfo);
                return undefined;
            }
            attachmentLabelsReponse.Body.ResponseMessages.Items.forEach(item => {
                if (item?.AttachmentLabel?.LabelId) {
                    const label = item.AttachmentLabel.LabelId;
                    attachmentClpLabelIds.push(label);
                }
            });
        }
        
        // Combine item and attachment label IDs
        const clpLabelIds: string[] = clpLabelId
            ? [clpLabelId, ...attachmentClpLabelIds]
            : [...attachmentClpLabelIds];
        
        if (clpLabelIds.length === 0) {
            // No CLP labels found
            return undefined;
        }
        
        if (!tenantId) {
            logGreyError("InaccurateCLPInfo_TenantIdUndefined", mailboxInfo);
            return undefined;
        }
        
        // Map each labelId to { clpLabelId, tenantId }
        const clpInfoArray: CLPInfo[] = clpLabelIds.map(labelId => ({
            clpLabelId: labelId,
            tenantId,
        }));
        cachedCLPInfo = clpInfoArray;
        return clpInfoArray;
    }
    catch (error) {
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
        // @ts-expect-error
        logGreyError("ErrorLoadingClpInfo", mailboxInfo, error);
        return undefined;
    }
}

export function getAttachmentIds(item: ClientItem | undefined): AttachmentId[] | undefined {
    if (!item) {
        return undefined;
    }
    if (!item.Attachments) {
        return [];
    }
    if (item.Attachments.some(att => att.AttachmentId === undefined)) {
        return undefined;
    }
    return item.Attachments.map(att => att.AttachmentId as AttachmentId);
}
